import React, { useState, createContext, ReactNode, useEffect } from "react";
import { defaultPermissions as defaultPermissionsConst } from "../../_constants/permissions";
import { StoredPermissions } from "@/app/@types/permissions";

import { useUserContext } from "@/app/_hooks/useUserContext";
import { prefetchUserAccountingIntegration } from "@/app/_firebase/user";
import { useGetPermissions } from "@/app/_hooks/useGetPermissions";
import AnimatedLoader from "@/app/_components/AnimatedLoader/AnimatedLoader";
// Define the types for the Permissions context
interface PermissionsContextProps {
  permissions: StoredPermissions;
  setPermissions: React.Dispatch<React.SetStateAction<StoredPermissions>>;
}

// Set default permissions based on the provided schema
const defaultPermissions: StoredPermissions = { ...defaultPermissionsConst };

// Create the Permissions Context
export const PermissionsContext = createContext<PermissionsContextProps>({
  permissions: defaultPermissions,
  setPermissions: () => {}, // Placeholder function
});

// Define the PermissionsProvider component
interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const user = useUserContext();
  const [permissions, setPermissions] =
    useState<StoredPermissions>(defaultPermissions);

  const {
    data: permissionsData,
    isLoading: getPermissionsIsLoading,
    isError: getPermissionsIsError,
    error: getPermissionsError,
  } = useGetPermissions();

  // AccountingProvider sits under this gate, so its first read used to wait
  // for permissions to finish. Start it now; it picks the result up when it
  // mounts. Same spinners, one round trip shorter on app open.
  useEffect(() => {
    if (user?.uid) prefetchUserAccountingIntegration(user.uid);
  }, [user?.uid]);

  // Serve the loaded permissions directly. This used to re-read the same doc
  // in an effect after the query had already returned it, so children
  // rendered with default permissions until that second read came back.
  const effectivePermissions = permissionsData ?? permissions;

  return (
    <PermissionsContext.Provider
      value={{ permissions: effectivePermissions, setPermissions }}
    >
      {getPermissionsIsLoading ? <AnimatedLoader bkgOpacity={1} /> : children}
    </PermissionsContext.Provider>
  );
};
